<template>
  <div class="h3">Группы услуг</div>
  <div class="paragraph-s_h mb-10 text-secondary mt-4">Добавьте группы услуг</div>

  <div v-if="!serviceGroups?.length" class="d-flex flex-column align-center mt-10 py-10">
    <v-img :src="ServiceGroupsImage" width="160px" />
    <div class="caption text-center mt-10">Отсутствуют группы услуг</div>

    <e-btn variant="link" class="mt-6" prepend-icon-src="plus" @click="createServiceGroup">Добавить группу услуг</e-btn>
  </div>

  <e-form v-else data-wide-content @submit="next">
    <e-data-items :items="serviceGroups" :headers="headers">
      <template v-slot:[`item.services`]="{ item }">
        <div v-for="service in item.services" :key="service.cid">
          {{ service.shortName }}
        </div>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <e-btn
          icon
          size="S"
          variant="ghost"
          class="ml-1 text-link"
          :loading="loadingServiceGroupCid === item.cid"
          @click="editServiceGroup(item.cid)"
        >
          <e-icon name="documentText" />
        </e-btn>
      </template>
    </e-data-items>
    <e-btn variant="link" class="mt-6 mr-auto" prepend-icon-src="plus" @click="createServiceGroup"
      >Добавить группу услуг</e-btn
    >
  </e-form>

  <div class="d-flex mt-10">
    <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
    <v-spacer />
    <e-btn
      variant="primary"
      type="submit"
      append-icon-src="arrowRight"
      :loading="isLoading"
      :disabled="!serviceGroups?.length"
      @click="next"
    >
      Далее
    </e-btn>
  </div>
  <create-service-group-dialog
    v-if="integration"
    v-model="isCreateDialogOpened"
    :tariff-schedule-cid="tariffScheduleCid"
    :hotel-set-cid="integration.master.hotelSetCid"
    @success="onAddServiceGroup"
  />
  <service-group-info-dialog
    v-if="integration"
    v-model="isServiceGroupInfoDialogOpened"
    :tariff-schedule-cid="tariffScheduleCid"
    :hotel-set-cid="integration.master.hotelSetCid"
    :service-group-cid="editServiceGroupCid"
    @loaded="loadingServiceGroupCid = undefined"
    @success="updateServiceGroupData"
  />
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue';
import type { AddServiceGroupPayload, ServiceGroup } from '@/parts/tariffs/store/serviceGroups';
import ServiceGroupsImage from '@/assets/img/common/service-group__placeholder.svg';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import { EBtn, EForm, EIcon } from '@/core/components/ui';
import CreateServiceGroupDialog from '@/parts/tariffs/components/ServiceGroups/CreateServiceGroupDialog.vue';
import ServiceGroupInfoDialog from '@/parts/tariffs/components/ServiceGroups/ServiceGroupInfoDialog.vue';
import { useReverseIntegrationStore } from '@/parts/tariffs/store/reverse';
import { useRoute } from 'vue-router';

const $route = useRoute();
const tariffScheduleCid = toRef(() => $route.query.t_id ? String($route.query.t_id) : undefined);
const { integration, activeStep, setActiveStep, updateServiceGroups } = useReverseIntegrationStore();

const serviceGroups = toRef(() => integration.value?.master?.steps.serviceGroupsSettings?.serviceGroups || []);

const isCreateDialogOpened = ref(false);
const isServiceGroupInfoDialogOpened = ref(false);
const editServiceGroupCid = ref<string>('');
const loadingServiceGroupCid = ref<string>();

const back = () => {
  setActiveStep(activeStep.value - 1);
};

const editServiceGroup = (cid: string) => {
  editServiceGroupCid.value = cid;
  loadingServiceGroupCid.value = cid;
  isServiceGroupInfoDialogOpened.value = true;
};

const createServiceGroup = () => {
  isCreateDialogOpened.value = true;
};

const onAddServiceGroup = (data: AddServiceGroupPayload) => {
  if (!integration.value?.master) {
    return;
  }

  if (data.addedServiceGroup) {
    integration.value.master.steps.serviceGroupsSettings!.serviceGroups.push({
      cid: data.addedServiceGroup.serviceGroupCid,
      name: data.addedServiceGroup.name,
      services: data.addedServiceGroup.services.map((service) => ({
        cid: service.cid,
        shortName: service.shortName,
      })),
    });
  }
};

const updateServiceGroupData = (data: ServiceGroup) => {
  if (!integration.value?.master || !data) {
    return;
  }

  const existingSG = serviceGroups.value.find((s) => s.cid === data.cid);

  if (existingSG) {
    existingSG.name = data.name;
    existingSG.services = data.services.map((service) => ({
      cid: service.cid,
      shortName: service.shortName,
    }));
  }
};

const isLoading = ref(false);

const next = async () => {
  if (!serviceGroups.value) {
    return;
  }

  isLoading.value = true;

  const response = await updateServiceGroups(serviceGroups.value.map((serviceGroup) => serviceGroup.cid));

  if (response.data) {
    setActiveStep(activeStep.value + 1);
  }

  isLoading.value = false;
};

const headers = [
  {
    key: 'name[0].value',
    title: 'Наименование',
  },
  {
    key: 'services',
    title: 'Включённые услуги',
    sortable: false,
  },
  {
    key: 'actions',
    sortable: false,
    width: 64,
  },
];
</script>

<style scoped lang="scss"></style>
